<script setup>
import ReportFilterSelector from './FilterSelector.vue';
import { formatTime } from '@chatwoot/utils';
import {
  useStore,
  useMapGetter,
  useStoreGetters,
} from 'dashboard/composables/store';
import Table from 'dashboard/components/table/Table.vue';
import { generateFileName } from 'dashboard/helper/downloadHelper';
import {
  getUserRole,
  getUserPermissions,
} from 'dashboard/helper/permissionsHelper';
import {
  useVueTable,
  createColumnHelper,
  getCoreRowModel,
} from '@tanstack/vue-table';
import { computed, onMounted, ref, h } from 'vue';

const props = defineProps({
  getterKey: {
    type: String,
    default: '',
  },
  actionKey: {
    type: String,
    default: '',
  },
  summaryKey: {
    type: String,
    default: '',
  },
  fetchItemsKey: {
    type: String,
    required: true,
  },
});

const store = useStore();
const getters = useStoreGetters();

const from = ref(0);
const to = ref(0);
const businessHours = ref(false);
import { useI18n } from 'vue-i18n';
import SummaryReportLink from './SummaryReportLink.vue';

const allRowItems = useMapGetter([props.getterKey]) || [];
const currentUser = computed(() => getters.getCurrentUser.value);
const currentAccountId = computed(() => getters.getCurrentAccountId.value);

const isCurrentUserAgent = computed(() => {
  if (!currentUser.value || !currentAccountId.value) return false;

  const role = getUserRole(currentUser.value, currentAccountId.value);
  const permissions = getUserPermissions(
    currentUser.value,
    currentAccountId.value
  );

  return role === 'agent' && !permissions.includes('access_all_reports');
});

const rowItems = computed(() => {
  if (!isCurrentUserAgent.value) {
    return allRowItems.value;
  }

  const currentUserId = currentUser.value?.id;
  if (!currentUserId) return allRowItems.value;

  return allRowItems.value.filter(item => item.id === currentUserId);
});
const reportMetrics = useMapGetter([props.summaryKey]) || [];

const getMetrics = id =>
  reportMetrics.value.find(metrics => metrics.id === Number(id)) || {};

// 'classic' (flat list) or 'teams' (rows grouped under each team)
const agentGrouping = ref('classic');

const teams = computed(() => store.getters['teams/getTeams'] || []);
const teamAgentsMap = computed(() => {
  const getTeamMembers = store.getters['teamMembers/getTeamMembers'];
  return teams.value.reduce((acc, team) => {
    acc[team.id] = (getTeamMembers(team.id) || []).map(member => member.id);
    return acc;
  }, {});
});

let teamDataLoaded = false;
const ensureTeamData = async () => {
  if (teamDataLoaded) return;
  teamDataLoaded = true;
  try {
    await store.dispatch('teams/get');
    const teamList = store.getters['teams/getTeams'] || [];
    await Promise.all(
      teamList.map(team =>
        store.dispatch('teamMembers/get', { teamId: team.id })
      )
    );
  } catch (error) {
    teamDataLoaded = false;
  }
};

const columnHelper = createColumnHelper();
const { t } = useI18n();

const defaultSpanRender = cellProps =>
  h(
    'span',
    {
      class: cellProps.getValue() ? '' : 'text-n-slate-12',
    },
    cellProps.getValue()
  );

const HeaderWithTooltip = {
  props: ['headerText', 'tooltipText'],
  template: `
    <div class="flex items-center content-center gap-1" v-tooltip.top="tooltipText">
      <span>
        {{ headerText }}&nbsp;
      </span>
      <div>
        <span class="i-lucide-info size-4"></span>
      </div>
    </div>
  `,
};

const columns = computed(() => [
  columnHelper.accessor('name', {
    header: t(`SUMMARY_REPORTS.AGENT`),
    width: 300,
    cell: cellProps => h(SummaryReportLink, cellProps),
  }),
  columnHelper.accessor('totalActivities', {
    header: () =>
      h(HeaderWithTooltip, {
        headerText: t('SUMMARY_REPORTS.TOTAL_ACTIVITIES'),
        tooltipText: t('SUMMARY_REPORTS.TOOLTIPS.TOTAL_ACTIVITIES'),
      }),
    width: 200,
    cell: defaultSpanRender,
  }),
  columnHelper.accessor('phoneCallCount', {
    header: () =>
      h(HeaderWithTooltip, {
        headerText: t('SUMMARY_REPORTS.PHONE_CALLS'),
        tooltipText: t('SUMMARY_REPORTS.TOOLTIPS.PHONE_CALLS'),
      }),
    width: 200,
    cell: defaultSpanRender,
  }),
  columnHelper.accessor('inboundCalls', {
    header: () =>
      h(HeaderWithTooltip, {
        headerText: t('SUMMARY_REPORTS.INBOUND_CALLS'),
        tooltipText: t('SUMMARY_REPORTS.TOOLTIPS.INBOUND_CALLS'),
      }),
    width: 200,
    cell: defaultSpanRender,
  }),
  columnHelper.accessor('outboundCalls', {
    header: () =>
      h(HeaderWithTooltip, {
        headerText: t('SUMMARY_REPORTS.OUTBOUND_CALLS'),
        tooltipText: t('SUMMARY_REPORTS.TOOLTIPS.OUTBOUND_CALLS'),
      }),
    width: 200,
    cell: defaultSpanRender,
  }),
  columnHelper.accessor('responseCountChat', {
    header: () =>
      h(HeaderWithTooltip, {
        headerText: t('SUMMARY_REPORTS.OUTGOING_CHATS'),
        tooltipText: t('SUMMARY_REPORTS.TOOLTIPS.OUTGOING_CHATS'),
      }),
    width: 200,
    cell: defaultSpanRender,
  }),
  columnHelper.accessor('responseCountEmail', {
    header: () =>
      h(HeaderWithTooltip, {
        headerText: t('SUMMARY_REPORTS.OUTGOING_EMAILS'),
        tooltipText: t('SUMMARY_REPORTS.TOOLTIPS.OUTGOING_EMAILS'),
      }),
    width: 200,
    cell: defaultSpanRender,
  }),
  columnHelper.accessor('onlineTime', {
    header: () =>
      h(HeaderWithTooltip, {
        headerText: t('SUMMARY_REPORTS.ONLINE_TIME'),
        tooltipText: t('SUMMARY_REPORTS.TOOLTIPS.ONLINE_TIME'),
      }),
    width: 200,
    cell: defaultSpanRender,
  }),
  columnHelper.accessor('queueAvailableTime', {
    header: () =>
      h(HeaderWithTooltip, {
        headerText: t('SUMMARY_REPORTS.QUEUE_AVAILABLE_TIME'),
        tooltipText: t('SUMMARY_REPORTS.TOOLTIPS.QUEUE_AVAILABLE_TIME'),
      }),
    width: 200,
    cell: defaultSpanRender,
  }),
  columnHelper.accessor('avgFirstResponseTime', {
    header: () =>
      h(HeaderWithTooltip, {
        headerText: t('SUMMARY_REPORTS.AVG_FIRST_RESPONSE_TIME'),
        tooltipText: t('SUMMARY_REPORTS.TOOLTIPS.AVG_FIRST_RESPONSE_TIME'),
      }),
    width: 200,
    cell: defaultSpanRender,
  }),
  columnHelper.accessor('avgResolutionTime', {
    header: () =>
      h(HeaderWithTooltip, {
        headerText: t('SUMMARY_REPORTS.AVG_RESOLUTION_TIME'),
        tooltipText: t('SUMMARY_REPORTS.TOOLTIPS.AVG_RESOLUTION_TIME'),
      }),
    width: 200,
    cell: defaultSpanRender,
  }),
  columnHelper.accessor('avgReplyTime', {
    header: () =>
      h(HeaderWithTooltip, {
        headerText: t('SUMMARY_REPORTS.AVG_REPLY_TIME'),
        tooltipText: t('SUMMARY_REPORTS.TOOLTIPS.AVG_REPLY_TIME'),
      }),
    width: 200,
    cell: defaultSpanRender,
  }),
]);

const renderAvgTime = value => (value ? formatTime(value) : '--');

const renderCount = value => (value ? value.toLocaleString() : '--');

const buildAgentRow = row => {
  const rowMetrics = getMetrics(row.id);
  const {
    responseCountChat,
    responseCountEmail,
    phoneCallCount,
    inboundCallCount,
    outboundCallCount,
    avgResolutionTime,
    avgFirstResponseTime,
    avgReplyTime,
    onlineTime,
    queueAvailableTime,
  } = rowMetrics;

  const totalActivities =
    (inboundCallCount || 0) +
    (outboundCallCount || 0) +
    (responseCountChat || 0) +
    (responseCountEmail || 0);

  return {
    id: row.id,
    name: row.name,
    type: 'agent',
    totalActivities: renderCount(totalActivities),
    phoneCallCount: renderCount(phoneCallCount),
    inboundCalls: renderCount(inboundCallCount),
    outboundCalls: renderCount(outboundCallCount),
    responseCountChat: renderCount(responseCountChat),
    responseCountEmail: renderCount(responseCountEmail),
    onlineTime: renderAvgTime(onlineTime),
    queueAvailableTime: renderAvgTime(queueAvailableTime),
    avgFirstResponseTime: renderAvgTime(avgFirstResponseTime),
    avgResolutionTime: renderAvgTime(avgResolutionTime),
    avgReplyTime: renderAvgTime(avgReplyTime),

    raw: {
      totalActivities,
      phoneCallCount: phoneCallCount || 0,
      inboundCallCount: inboundCallCount || 0,
      outboundCallCount: outboundCallCount || 0,
      responseCountChat: responseCountChat || 0,
      responseCountEmail: responseCountEmail || 0,
      onlineTime: onlineTime || 0,
      queueAvailableTime: queueAvailableTime || 0,
      avgFirstResponseTime: avgFirstResponseTime || 0,
      avgResolutionTime: avgResolutionTime || 0,
      avgReplyTime: avgReplyTime || 0,
    },
  };
};

const buildSummaryRow = (agentRows, { id, name, type }) => {
  const sums = agentRows.reduce(
    (acc, row) => {
      acc.totalActivities += row.raw.totalActivities || 0;
      acc.phoneCallCount += row.raw.phoneCallCount;
      acc.inboundCallCount += row.raw.inboundCallCount;
      acc.outboundCallCount += row.raw.outboundCallCount;
      acc.responseCountChat += row.raw.responseCountChat;
      acc.responseCountEmail += row.raw.responseCountEmail;
      acc.onlineTime += row.raw.onlineTime;
      acc.queueAvailableTime += row.raw.queueAvailableTime;
      return acc;
    },
    {
      totalActivities: 0,
      phoneCallCount: 0,
      inboundCallCount: 0,
      outboundCallCount: 0,
      responseCountChat: 0,
      responseCountEmail: 0,
      onlineTime: 0,
      queueAvailableTime: 0,
    }
  );

  return {
    id,
    name,
    type,
    totalActivities: renderCount(sums.totalActivities),
    phoneCallCount: renderCount(sums.phoneCallCount),
    inboundCalls: renderCount(sums.inboundCallCount),
    outboundCalls: renderCount(sums.outboundCallCount),
    responseCountChat: renderCount(sums.responseCountChat),
    responseCountEmail: renderCount(sums.responseCountEmail),
    onlineTime: renderAvgTime(sums.onlineTime),
    queueAvailableTime: renderAvgTime(sums.queueAvailableTime),
    avgFirstResponseTime: '--',
    avgResolutionTime: '--',
    avgReplyTime: '--',

    raw: {
      ...sums,
      avgFirstResponseTime: 0,
      avgResolutionTime: 0,
      avgReplyTime: 0,
    },
  };
};

const buildTeamHeaderRow = ({ id, name, rowClass = '' }) => ({
  id: `team-${id}`,
  name,
  type: 'team-header',
  rowClass,
  totalActivities: '',
  phoneCallCount: '',
  inboundCalls: '',
  outboundCalls: '',
  responseCountChat: '',
  responseCountEmail: '',
  onlineTime: '',
  queueAvailableTime: '',
  avgFirstResponseTime: '',
  avgResolutionTime: '',
  avgReplyTime: '',
  raw: {},
});

const buildClassicRows = agentRows => {
  if (!agentRows.length) return [];
  return [
    ...agentRows,
    buildSummaryRow(agentRows, {
      id: 'sum',
      name: t('SUMMARY_REPORTS.TOTAL'),
      type: 'sum',
    }),
  ];
};

const buildTeamGroupedRows = agentRows => {
  if (!agentRows.length) return [];

  const rows = [];
  const map = teamAgentsMap.value;
  const assigned = new Set();

  // extra top spacing to separate groups — but not above the very first group
  const headerRowClass = () => (rows.length ? 'pt-10' : '');

  teams.value.forEach(team => {
    const memberIds = new Set(map[team.id] || []);
    const teamRows = agentRows.filter(row => memberIds.has(row.id));
    if (!teamRows.length) return;

    teamRows.forEach(row => assigned.add(row.id));
    rows.push(
      buildTeamHeaderRow({
        id: team.id,
        name: team.name,
        rowClass: headerRowClass(),
      })
    );
    rows.push(...teamRows);
    rows.push(
      buildSummaryRow(teamRows, {
        id: `subtotal-${team.id}`,
        name: t('SUMMARY_REPORTS.SUBTOTAL'),
        type: 'subtotal',
      })
    );
  });

  // agents that don't belong to any team
  const noTeamRows = agentRows.filter(row => !assigned.has(row.id));
  if (noTeamRows.length) {
    rows.push(
      buildTeamHeaderRow({
        id: 'no-team',
        name: t('SUMMARY_REPORTS.NO_TEAM'),
        rowClass: headerRowClass(),
      })
    );
    rows.push(...noTeamRows);
    rows.push(
      buildSummaryRow(noTeamRows, {
        id: 'subtotal-no-team',
        name: t('SUMMARY_REPORTS.SUBTOTAL'),
        type: 'subtotal',
      })
    );
  }

  // grand total across all unique agents
  rows.push(
    buildSummaryRow(agentRows, {
      id: 'sum',
      name: t('SUMMARY_REPORTS.TOTAL'),
      type: 'sum',
    })
  );

  return rows;
};

const tableData = computed(() => {
  const agentRows = rowItems.value.map(buildAgentRow);

  return agentGrouping.value === 'teams'
    ? buildTeamGroupedRows(agentRows)
    : buildClassicRows(agentRows);
});

const fetchAllData = () => {
  store.dispatch(props.fetchItemsKey);
  store.dispatch(props.actionKey, {
    since: from.value,
    until: to.value,
    businessHours: businessHours.value,
  });
};

onMounted(() => fetchAllData());

const onFilterChange = updatedFilter => {
  from.value = updatedFilter.from;
  to.value = updatedFilter.to;
  businessHours.value = updatedFilter.businessHours;
  if (updatedFilter.agentGrouping) {
    agentGrouping.value = updatedFilter.agentGrouping;
  }
  if (agentGrouping.value === 'teams') {
    ensureTeamData();
  }
  fetchAllData();
};

const table = useVueTable({
  get data() {
    return tableData.value;
  },
  get columns() {
    return columns.value;
  },
  enableSorting: false,
  getCoreRowModel: getCoreRowModel(),
});

// downloadReports method is not used in this component
// but it is exposed to be used in the parent component
const downloadReports = () => {
  const fileName = generateFileName({
    type: 'agents',
    to: to.value,
    businessHours: businessHours.value,
  });
  const params = {
    from: from.value,
    to: to.value,
    fileName,
    businessHours: businessHours.value,
  };
  store.dispatch('downloadAgentReports', params);
};

defineExpose({ downloadReports });
</script>

<template>
  <ReportFilterSelector show-agent-grouping @filter-change="onFilterChange" />
  <div
    class="flex-1 overflow-auto px-5 py-6 mt-5 shadow outline-1 outline outline-n-container rounded-xl bg-n-solid-2"
  >
    <Table :table="table" />
  </div>
</template>
